import { useState, useEffect, useCallback } from 'react';
import { MODULE_SCHEMAS, DEFAULT_MASTER_META, DEMO_FIXTURES } from '../../data/masterData';
import { safeGet, safeSet, safeRemove } from '../../utils/safeStorage';
import { STORAGE_KEYS } from './storageKeys';

// ---- Defaults ----
// Per "no auto-creation" rule: only Risk ships with seeded version history.
// PCC / Fraud / DLC history begins empty and grows as Admin (mock-)uploads masters.
const DEFAULT_HISTORY = {
  risk: [
    { version: 'v3.2', publishedAt: '2025-07-22 10:14', publishedBy: 'Aakash Sharma', recordCount: 6, status: 'Active', notes: 'Q3 2025 quarterly refresh' },
    { version: 'v3.1', publishedAt: '2025-04-15 09:00', publishedBy: 'Aakash Sharma', recordCount: 5, status: 'Archived', notes: 'Q2 2025 cycle' },
    { version: 'v3.0', publishedAt: '2025-01-10 14:20', publishedBy: 'Aakash Sharma', recordCount: 4, status: 'Archived', notes: 'Annual baseline' },
  ],
  pcc: [],
  fraud: [],
  dlc: [],
};

const cloneSamples = () => ({
  risk: MODULE_SCHEMAS.risk.sampleRows.map((r) => ({ ...r })),
  pcc: MODULE_SCHEMAS.pcc.sampleRows.map((r) => ({ ...r })),
  fraud: MODULE_SCHEMAS.fraud.sampleRows.map((r) => ({ ...r })),
  dlc: MODULE_SCHEMAS.dlc.sampleRows.map((r) => ({ ...r })),
});

const cloneMeta = () => ({
  risk: { ...DEFAULT_MASTER_META.risk },
  pcc: { ...DEFAULT_MASTER_META.pcc },
  fraud: { ...DEFAULT_MASTER_META.fraud },
  dlc: { ...DEFAULT_MASTER_META.dlc },
});

// Bump v1.2 → v1.3, drop -draft suffix.
function bumpVersion(v) {
  if (!v) return 'v1.1';
  const m = v.match(/^v?(\d+)\.(\d+)(?:-\w+)?$/);
  if (!m) return 'v1.1';
  return `v${parseInt(m[1], 10)}.${parseInt(m[2], 10) + 1}`;
}

const fmtTs = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export function useMastersState({ user }) {
  const [masters, setMasters] = useState(() => safeGet(STORAGE_KEYS.masters, cloneSamples()));
  const [masterMeta, setMasterMeta] = useState(() => safeGet(STORAGE_KEYS.meta, cloneMeta()));
  const [versionHistory, setVersionHistory] = useState(() => safeGet(STORAGE_KEYS.history, DEFAULT_HISTORY));
  const [pendingApprovals, setPendingApprovals] = useState(() => safeGet(STORAGE_KEYS.pendingApprovals, []));
  // Per-module per-version row snapshots — drives the Schema Diff Viewer.
  // Shape: { [moduleKey]: { [version]: { rows, headers, columnMap, capturedAt } } }
  const [versionSnapshots, setVersionSnapshots] = useState(() => safeGet(STORAGE_KEYS.versionSnapshots, {}));

  useEffect(() => { safeSet(STORAGE_KEYS.masters, masters); }, [masters]);
  useEffect(() => { safeSet(STORAGE_KEYS.meta, masterMeta); }, [masterMeta]);
  useEffect(() => { safeSet(STORAGE_KEYS.history, versionHistory); }, [versionHistory]);
  useEffect(() => { safeSet(STORAGE_KEYS.pendingApprovals, pendingApprovals); }, [pendingApprovals]);
  useEffect(() => { safeSet(STORAGE_KEYS.versionSnapshots, versionSnapshots); }, [versionSnapshots]);

  // Capture a deep clone of `rows` against `(moduleKey, version)` so the diff
  // viewer can later compare v(n) ↔ v(n+1). Idempotent on the same version.
  const snapshotVersion = useCallback((moduleKey, version, rows, extras = {}) => {
    setVersionSnapshots((s) => ({
      ...s,
      [moduleKey]: {
        ...(s[moduleKey] || {}),
        [version]: {
          rows: rows.map((r) => ({ ...r })),
          headers: extras.headers || null,
          columnMap: extras.columnMap || null,
          fileName: extras.fileName || null,
          capturedAt: fmtTs(new Date()),
        },
      },
    }));
  }, []);

  const setMasterRows = useCallback((moduleKey, rows) => {
    setMasters((m) => ({ ...m, [moduleKey]: rows }));
  }, []);

  // Direct save (Admin path) — merges, bumps version, archives prior, logs history.
  const saveMasterUpload = useCallback((moduleKey, newRows, meta) => {
    const schema = MODULE_SCHEMAS[moduleKey];
    let added = 0, updated = 0, totalCount = 0;

    setMasters((mst) => {
      const existing = mst[moduleKey] || [];
      const existingIds = new Set(existing.map((r) => r[schema.idField]));
      const merged = [...existing];
      newRows.forEach((nr) => {
        const id = nr[schema.idField];
        if (existingIds.has(id)) {
          const idx = merged.findIndex((r) => r[schema.idField] === id);
          merged[idx] = { ...merged[idx], ...nr };
          updated++;
        } else {
          merged.push(nr);
          added++;
        }
      });
      totalCount = merged.length;
      // Snapshot the post-merge state against the new version — fires from
      // inside the setter so we use the freshly-computed `merged`.
      const prevVer = (masterMeta[moduleKey] || {}).version;
      const nextVer = (meta?.published !== false) ? bumpVersion(prevVer) : `${prevVer || 'v1.0'}-draft`;
      snapshotVersion(moduleKey, nextVer, merged, {
        headers: meta?.headers,
        columnMap: meta?.columnMap,
        fileName: meta?.fileName,
      });
      return { ...mst, [moduleKey]: merged };
    });

    const ts = fmtTs(new Date());
    const published = meta?.published !== false;

    setMasterMeta((m) => {
      const prev = m[moduleKey] || {};
      const nextVersion = published ? bumpVersion(prev.version) : `${prev.version || 'v1.0'}-draft`;
      return {
        ...m,
        [moduleKey]: {
          status: published ? 'Active' : 'Draft',
          lastUploadedAt: ts,
          lastUploadedBy: user?.name || 'Aakash Sharma',
          fileName: meta?.fileName || prev.fileName,
          version: nextVersion,
          columnMap: meta?.columnMap,
          headers: meta?.headers,
          sheetNames: meta?.sheetNames || prev.sheetNames || [],
        },
      };
    });

    setVersionHistory((h) => {
      const prev = masterMeta[moduleKey] || {};
      const nextVersion = published ? bumpVersion(prev.version) : `${prev.version || 'v1.0'}-draft`;
      const prevHist = (h[moduleKey] || []).map((v) => v.status === 'Active' && published ? { ...v, status: 'Archived' } : v);
      return {
        ...h,
        [moduleKey]: [
          {
            version: nextVersion,
            publishedAt: ts,
            publishedBy: user?.name || 'Aakash Sharma',
            recordCount: totalCount,
            status: published ? 'Active' : 'Draft',
            notes: meta?.fileName ? `Uploaded ${meta.fileName}` : 'Manual update',
            added,
            updated,
          },
          ...prevHist,
        ],
      };
    });

    return { added, updated, total: totalCount, published };
  // saveMasterUpload now depends on snapshotVersion
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, masterMeta, snapshotVersion]);

  // ---------- Approval Workflow ----------
  const submitMasterForApproval = useCallback((moduleKey, newRows, meta) => {
    const schema = MODULE_SCHEMAS[moduleKey];
    const existing = masters[moduleKey] || [];
    const existingIds = new Set(existing.map((r) => r[schema.idField]));
    let added = 0, updated = 0;
    newRows.forEach((nr) => {
      if (existingIds.has(nr[schema.idField])) updated++;
      else added++;
    });
    const prev = masterMeta[moduleKey] || {};
    const submission = {
      id: `MA-${moduleKey.toUpperCase()}-${Date.now()}`,
      moduleKey,
      moduleLabel: schema.label,
      submittedBy: user?.name || 'Aakash Sharma',
      submittedRole: user?.role || 'Admin',
      submittedAt: fmtTs(new Date()),
      proposedVersion: bumpVersion(prev.version),
      currentVersion: prev.version || 'v1.0',
      newRows,
      meta,
      added,
      updated,
      status: 'Pending',
      reviewer: 'Legal Counsel',
      comments: [],
    };
    setPendingApprovals((arr) => [submission, ...arr]);
    setMasterMeta((m) => ({
      ...m,
      [moduleKey]: { ...(m[moduleKey] || {}), status: 'Pending', pendingSubmissionId: submission.id },
    }));
    return submission;
  }, [masters, masterMeta, user]);

  const approveMasterSubmission = useCallback((submissionId, reviewerComment) => {
    setPendingApprovals((arr) => {
      const sub = arr.find((s) => s.id === submissionId);
      if (!sub || sub.status !== 'Pending') return arr;

      const schema = MODULE_SCHEMAS[sub.moduleKey];
      const existing = masters[sub.moduleKey] || [];
      const existingIds = new Set(existing.map((r) => r[schema.idField]));
      const merged = [...existing];
      sub.newRows.forEach((nr) => {
        const id = nr[schema.idField];
        if (existingIds.has(id)) {
          const idx = merged.findIndex((r) => r[schema.idField] === id);
          merged[idx] = { ...merged[idx], ...nr };
        } else {
          merged.push(nr);
        }
      });

      setMasters((mst) => ({ ...mst, [sub.moduleKey]: merged }));
      // Snapshot the merged set against the newly-approved version.
      snapshotVersion(sub.moduleKey, sub.proposedVersion, merged, {
        headers: sub.meta?.headers,
        columnMap: sub.meta?.columnMap,
        fileName: sub.meta?.fileName,
      });

      const ts = fmtTs(new Date());

      setMasterMeta((m) => ({
        ...m,
        [sub.moduleKey]: {
          status: 'Active',
          lastUploadedAt: ts,
          lastUploadedBy: sub.submittedBy,
          fileName: sub.meta?.fileName,
          version: sub.proposedVersion,
          columnMap: sub.meta?.columnMap,
          headers: sub.meta?.headers,
          sheetNames: sub.meta?.sheetNames || [],
          pendingSubmissionId: null,
        },
      }));

      setVersionHistory((h) => {
        const prevHist = (h[sub.moduleKey] || []).map((v) => v.status === 'Active' ? { ...v, status: 'Archived' } : v);
        return {
          ...h,
          [sub.moduleKey]: [
            {
              version: sub.proposedVersion,
              publishedAt: ts,
              publishedBy: sub.submittedBy,
              recordCount: merged.length,
              status: 'Active',
              notes: `Approved by ${user?.name || 'Reviewer'}${reviewerComment ? ' — ' + reviewerComment : ''}`,
              added: sub.added,
              updated: sub.updated,
              approvedBy: user?.name || 'Reviewer',
            },
            ...prevHist,
          ],
        };
      });

      return arr.map((s) => s.id === submissionId
        ? { ...s, status: 'Approved', approvedAt: ts, approvedBy: user?.name || 'Reviewer', reviewerComment }
        : s);
    });
  }, [masters, user, snapshotVersion]);

  const rejectMasterSubmission = useCallback((submissionId, reason) => {
    setPendingApprovals((arr) => arr.map((s) => {
      if (s.id !== submissionId || s.status !== 'Pending') return s;
      const ts = fmtTs(new Date());
      setMasterMeta((m) => ({
        ...m,
        [s.moduleKey]: { ...(m[s.moduleKey] || {}), status: 'Active', pendingSubmissionId: null },
      }));
      return { ...s, status: 'Rejected', rejectedAt: ts, rejectedBy: user?.name || 'Reviewer', rejectionReason: reason };
    }));
  }, [user]);

  const addApprovalComment = useCallback((submissionId, text) => {
    setPendingApprovals((arr) => arr.map((s) => s.id === submissionId
      ? { ...s, comments: [...(s.comments || []), { author: user?.name || 'Reviewer', text, at: new Date().toISOString() }] }
      : s));
  }, [user]);

  const resetMasters = useCallback(() => {
    safeRemove(STORAGE_KEYS.masters);
    safeRemove(STORAGE_KEYS.meta);
    safeRemove(STORAGE_KEYS.history);
    safeRemove(STORAGE_KEYS.pendingApprovals);
    safeRemove(STORAGE_KEYS.responses);
    safeRemove(STORAGE_KEYS.versionSnapshots);
    setMasters(cloneSamples());
    setMasterMeta(cloneMeta());
    setVersionHistory(DEFAULT_HISTORY);
    setPendingApprovals([]);
    setVersionSnapshots({});
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // loadDemoMaster — Admin-only "mock-upload" of HMSI's official Excel
  // master for PCC / Fraud / DLC. Writes DEMO_FIXTURES rows into the master
  // store with provenance (`_sourceFile` + `_sourceSheet`), stamps fileName
  // and version on the meta, and appends a v1.0 entry to version history.
  // This is what proves the "no auto-creation" rule: questions only appear
  // for Department Heads AFTER an Admin has loaded a master.
  // ───────────────────────────────────────────────────────────────────────
  const loadDemoMaster = useCallback((moduleKey) => {
    const fixture = DEMO_FIXTURES[moduleKey];
    if (!fixture) return null;
    const ts = fmtTs(new Date());
    const fileName = fixture.fileName;
    const version = 'v1.0';
    const taggedRows = fixture.rows.map((r) => ({ ...r, _sourceFile: fileName, _sourceSheet: r._sourceSheet || 'Sheet1' }));

    setMasters((m) => ({ ...m, [moduleKey]: taggedRows }));
    snapshotVersion(moduleKey, version, taggedRows, { fileName });
    setMasterMeta((m) => ({
      ...m,
      [moduleKey]: {
        status: 'Active',
        lastUploadedAt: ts,
        lastUploadedBy: user?.name || 'Aakash Sharma',
        fileName,
        version,
        sheetNames: fixture.sheetNames || [],
      },
    }));
    setVersionHistory((h) => ({
      ...h,
      [moduleKey]: [
        {
          version,
          publishedAt: ts,
          publishedBy: user?.name || 'Aakash Sharma',
          recordCount: taggedRows.length,
          status: 'Active',
          notes: `Mock-uploaded ${fileName} · ${(fixture.sheetNames || []).length} sheet(s)`,
          added: taggedRows.length,
          updated: 0,
        },
      ],
    }));
    return { added: taggedRows.length, fileName, version };
  }, [user, snapshotVersion]);

  return {
    masters,
    masterMeta,
    versionHistory,
    versionSnapshots,
    pendingApprovals,
    setMasterRows,
    saveMasterUpload,
    submitMasterForApproval,
    approveMasterSubmission,
    rejectMasterSubmission,
    addApprovalComment,
    resetMasters,
    loadDemoMaster,
  };
}
